import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  TextInput,
  PasswordInput,
  Button,
  Stack,
  Alert,
  Text,
  LoadingOverlay,
  Box
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { IconAlertCircle, IconUser, IconMail, IconLock } from '@tabler/icons-react'
import { registerUser, clearError } from '../../store/slices/authSlice'

function RegisterForm({ onSuccess, onSwitchToLogin }) {
  const dispatch = useDispatch()
  const { loading, error } = useSelector((state) => state.auth)
  const [submitAttempted, setSubmitAttempted] = useState(false)

  const form = useForm({
    initialValues: {
      username: '',
      email: '',
      password: '',
      password_confirm: ''
    },
    validate: {
      username: (value) => {
        if (!value || value.length < 3) return 'Username must be at least 3 characters'
        if (!/^[a-zA-Z0-9_]+$/.test(value)) return 'Username can only contain letters, numbers, and underscores'
        return null
      },
      email: (value) => {
        if (!value) return 'Email is required'
        if (!/^\S+@\S+\.\S+$/.test(value)) return 'Please enter a valid email address'
        return null
      },
      password: (value) => {
        if (!value) return 'Password is required'
        if (value.length < 8) return 'Password must be at least 8 characters long'
        if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(value)) {
          return 'Password must contain at least one uppercase letter, one lowercase letter, and one number'
        }
        return null
      },
      password_confirm: (value, values) => {
        if (!value) return 'Password confirmation is required'
        if (value !== values.password) return 'Passwords do not match'
        return null
      }
    }
  })

  const handleSubmit = async (values) => {
    setSubmitAttempted(true)
    
    if (error) {
      dispatch(clearError())
    }

    try {
      const result = await dispatch(registerUser(values))
      if (registerUser.fulfilled.match(result)) {
        form.reset()
        setSubmitAttempted(false)
        if (onSuccess) {
          onSuccess(result.payload)
        }
      }
    } catch (err) {
      console.error('Registration failed:', err)
    }
  }

  return (
    <Box pos="relative">
      <LoadingOverlay visible={loading} />
      
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <Text size="lg" fw={600} ta="center" c="blue">
            Create Your Account
          </Text>

          {error && submitAttempted && (
            <Alert
              icon={<IconAlertCircle size={16} />}
              color="red"
              title="Registration Failed"
              withCloseButton
              onClose={() => dispatch(clearError())}
            >
              {error}
            </Alert>
          )}

          <TextInput
            label="Username"
            placeholder="Enter your username"
            leftSection={<IconUser size={16} />}
            required
            {...form.getInputProps('username')}
            error={form.errors.username}
          />

          <TextInput
            label="Email"
            placeholder="Enter your email"
            leftSection={<IconMail size={16} />}
            type="email"
            required
            {...form.getInputProps('email')}
            error={form.errors.email}
          />

          <PasswordInput
            label="Password"
            placeholder="Enter your password"
            leftSection={<IconLock size={16} />}
            required
            {...form.getInputProps('password')}
            error={form.errors.password}
          />

          <PasswordInput
            label="Confirm Password"
            placeholder="Confirm your password"
            leftSection={<IconLock size={16} />}
            required
            {...form.getInputProps('password_confirm')}
            error={form.errors.password_confirm}
          />

          <Button
            type="submit"
            fullWidth
            loading={loading}
            disabled={loading}
            size="md"
          >
            Create Account
          </Button>

          <Text size="sm" ta="center" c="dimmed">
            Already have an account?{' '}
            <Text
              component="button"
              type="button"
              variant="link"
              c="blue"
              fw={500}
              onClick={onSwitchToLogin}
              style={{ 
                background: 'none', 
                border: 'none', 
                cursor: 'pointer',
                textDecoration: 'underline'
              }}
            >
              Sign in here
            </Text>
          </Text>
        </Stack>
      </form>
    </Box>
  )
}

export default RegisterForm